import React, { useState, useRef, useEffect } from 'react';
import { LayoutDashboard, Plus, Inbox } from 'lucide-react';
import {
  Button,
  Input,
  Textarea,
  Select,
  Checkbox,
  Toggle,
  Badge,
  Avatar,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  StatCard,
  DescriptionList,
  EmptyState,
  Skeleton,
  Tabs,
  TabPanel,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  ConfirmDialog,
  Dropdown,
  Tooltip,
  Alert,
  FormSection,
  FormActions,
  CopyToClipboard,
  PageHeader,
  Container,
} from '../../design-system/components';
import {
  colors,
  semanticColors,
  fontSizes,
  fontWeights,
  spacing,
  borderRadius,
  shadows,
} from '../../design-system/tokens';

// ─── Section wrapper ────────────────────────────────────────────────
function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-20 mb-16">
      <h2 className="text-2xl font-semibold text-fg mb-6 pb-2 border-b border-edge-strong">
        {title}
      </h2>
      {children}
    </section>
  );
}
function Subsection({ title, children }) {
  return (
    <div className="mb-8">
      <h3 className="text-lg font-medium text-fg-secondary mb-4">{title}</h3>
      {children}
    </div>
  );
}
function Preview({ label, children }) {
  return (
    <div className="border border-edge-strong rounded-lg p-6 bg-surface">
      {label && (
        <p className="text-xs font-medium text-fg-tertiary uppercase tracking-wider mb-4">
          {label}
        </p>
      )}
      {children}
    </div>
  );
}
function CodeBlock({ children }) {
  return (
    <pre className="mt-3 rounded-lg bg-surface-secondary border border-edge-strong p-4 overflow-x-auto">
      <code className="text-sm font-mono text-fg-secondary whitespace-pre">{children}</code>
    </pre>
  );
}

// ─── Sidebar navigation ─────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    heading: 'Brand',
    items: [
      {
        id: 'brand',
        label: 'Brand',
      },
    ],
  },
  {
    heading: 'Tokens',
    items: [
      {
        id: 'colors',
        label: 'Colors',
      },
      {
        id: 'typography',
        label: 'Typography',
      },
      {
        id: 'spacing',
        label: 'Spacing',
      },
      {
        id: 'borders',
        label: 'Border Radius',
      },
      {
        id: 'shadows',
        label: 'Shadows',
      },
    ],
  },
  {
    heading: 'Primitives',
    items: [
      {
        id: 'button',
        label: 'Button',
      },
      {
        id: 'input',
        label: 'Input',
      },
      {
        id: 'textarea',
        label: 'Textarea',
      },
      {
        id: 'select',
        label: 'Select',
      },
      {
        id: 'checkbox',
        label: 'Checkbox',
      },
      {
        id: 'toggle',
        label: 'Toggle',
      },
      {
        id: 'badge',
        label: 'Badge',
      },
      {
        id: 'avatar',
        label: 'Avatar',
      },
    ],
  },
  {
    heading: 'Layout',
    items: [
      {
        id: 'container',
        label: 'Container',
      },
      {
        id: 'page-header',
        label: 'PageHeader',
      },
    ],
  },
  {
    heading: 'Data Display',
    items: [
      {
        id: 'card',
        label: 'Card',
      },
      {
        id: 'stat-card',
        label: 'StatCard',
      },
      {
        id: 'description-list',
        label: 'DescriptionList',
      },
      {
        id: 'empty-state',
        label: 'EmptyState',
      },
      {
        id: 'skeleton',
        label: 'Skeleton',
      },
      {
        id: 'tabs',
        label: 'Tabs',
      },
    ],
  },
  {
    heading: 'Feedback',
    items: [
      {
        id: 'alert',
        label: 'Alert',
      },
      {
        id: 'modal',
        label: 'Modal',
      },
      {
        id: 'confirm-dialog',
        label: 'ConfirmDialog',
      },
      {
        id: 'dropdown',
        label: 'Dropdown',
      },
      {
        id: 'tooltip',
        label: 'Tooltip',
      },
    ],
  },
  {
    heading: 'Forms',
    items: [
      {
        id: 'form-section',
        label: 'FormSection',
      },
      {
        id: 'form-actions',
        label: 'FormActions',
      },
      {
        id: 'copy-to-clipboard',
        label: 'CopyToClipboard',
      },
    ],
  },
];

// ─── Main Component ──────────────────────────────────────────────────
export default function DesignSystemIndex() {
  const [activeSection, setActiveSection] = useState('colors');
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const contentRef = useRef(null);

  // Intersection observer to highlight active nav item
  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      {
        rootMargin: '-80px 0px -60% 0px',
      },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);
  return (
    <div className="min-h-screen bg-surface-secondary">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-surface-inverse text-white">
        <div className="max-w-screen-2xl mx-auto px-6 py-4 flex items-center gap-4">
          <div className="w-9 h-9 rounded-xl bg-primary-500 flex items-center justify-center">
            <LayoutDashboard size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Speakflow Design System</h1>
            <p className="text-sm text-fg-tertiary">Components, tokens, and patterns</p>
          </div>
        </div>
      </header>

      <div className="max-w-screen-2xl mx-auto flex">
        {/* Sidebar */}
        <nav className="hidden lg:block w-64 shrink-0 sticky top-[73px] h-[calc(100vh-73px)] overflow-y-auto border-r border-edge-strong bg-surface py-6 px-4">
          {NAV_SECTIONS.map((group) => (
            <div key={group.heading} className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-fg-tertiary mb-2 px-2">
                {group.heading}
              </p>
              {group.items.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`block px-2 py-1.5 text-sm rounded-md transition-colors ${activeSection === item.id ? 'bg-surface-muted text-fg font-medium' : 'text-fg-secondary hover:text-fg hover:bg-surface-secondary'}`}
                >
                  {item.label}
                </a>
              ))}
            </div>
          ))}
        </nav>

        {/* Content */}
        <main ref={contentRef} className="flex-1 min-w-0 px-6 lg:px-12 py-10">
          {/* ═══ BRAND ═══ */}
          <BrandSection />

          {/* ═══ TOKENS ═══ */}
          <ColorSection />
          <TypographySection />
          <SpacingSection />
          <BorderRadiusSection />
          <ShadowSection />

          {/* ═══ PRIMITIVES ═══ */}
          <ButtonSection />
          <InputSection />
          <TextareaSection />
          <SelectSection />
          <CheckboxSection />
          <ToggleSection />
          <BadgeSection />
          <AvatarSection />

          {/* ═══ LAYOUT ═══ */}
          <ContainerSection />
          <PageHeaderSection />

          {/* ═══ DATA DISPLAY ═══ */}
          <CardSection />
          <StatCardSection />
          <DescriptionListSection />
          <EmptyStateSection />
          <SkeletonSection />
          <TabsSection />

          {/* ═══ FEEDBACK ═══ */}
          <AlertSection />
          <ModalSection showModal={showModal} setShowModal={setShowModal} />
          <ConfirmDialogSection showConfirm={showConfirm} setShowConfirm={setShowConfirm} />
          <DropdownSection />
          <TooltipSection />

          {/* ═══ FORMS ═══ */}
          <FormSectionSection />
          <FormActionsSection />
          <CopyToClipboardSection />
        </main>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TOKEN SECTIONS
// ═══════════════════════════════════════════════════════════════════════

function BrandSection() {
  return (
    <Section id="brand" title="Brand">
      <Subsection title="Brand Colors">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Brand', hex: '#fb2f57', className: 'bg-primary-500' },
            { name: 'Near Black', hex: '#111111', className: 'bg-surface-inverse' },
          ].map((color) => (
            <div key={color.name}>
              <div className={`h-20 rounded-lg ${color.className}`} />
              <p className="text-sm font-medium text-fg-secondary mt-2">{color.name}</p>
              <p className="text-xs text-fg-tertiary font-mono">{color.hex}</p>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection title="Display Typography">
        <Preview>
          <p className="text-5xl font-extrabold text-fg tracking-tight">
            The Speakflow Affiliate Program
          </p>
          <p className="mt-3 text-lg text-fg-tertiary">
            Earn commissions by referring customers to Speakflow.
          </p>
        </Preview>
      </Subsection>
    </Section>
  );
}

function ColorSection() {
  const colorScales = ['primary', 'secondary', 'success', 'warning', 'danger', 'neutral'];
  const shades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
  return (
    <Section id="colors" title="Colors">
      <Subsection title="Color Palette">
        <div className="space-y-6">
          {colorScales.map((scale) => (
            <div key={scale}>
              <p className="text-sm font-medium text-fg-secondary mb-2 capitalize">{scale}</p>
              <div className="grid grid-cols-11 gap-1">
                {shades.map((shade) => {
                  const hex = colors[scale][shade];
                  return (
                    <div key={shade} className="text-center">
                      <div
                        className="h-12 rounded-md border border-edge-strong"
                        style={{
                          backgroundColor: hex,
                        }}
                      />
                      <p className="text-[10px] text-fg-tertiary mt-1">{shade}</p>
                      <p className="text-[10px] text-fg-tertiary font-mono">{hex}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection title="Semantic Colors">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(semanticColors).map(([name, value]) => (
            <div
              key={name}
              className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-edge-strong"
            >
              <div
                className="w-8 h-8 rounded-md border border-edge-strong shrink-0"
                style={{
                  backgroundColor: value,
                }}
              />
              <div>
                <p className="text-xs font-medium text-fg-secondary">{name}</p>
                <p className="text-[10px] text-fg-tertiary font-mono">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </Subsection>
    </Section>
  );
}
function TypographySection() {
  return (
    <Section id="typography" title="Typography">
      <Subsection title="Font Sizes">
        <div className="space-y-4 bg-surface rounded-lg border border-edge-strong p-6">
          {Object.entries(fontSizes).map(([name, value]) => (
            <div key={name} className="flex items-baseline gap-6">
              <span className="w-16 text-xs text-fg-tertiary font-mono shrink-0">{name}</span>
              <span className="w-20 text-xs text-fg-tertiary shrink-0">{value}</span>
              <span
                style={{
                  fontSize: value,
                }}
              >
                The quick brown fox jumps over the lazy dog
              </span>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection title="Font Weights">
        <div className="space-y-3 bg-surface rounded-lg border border-edge-strong p-6">
          {Object.entries(fontWeights).map(([name, value]) => (
            <div key={name} className="flex items-baseline gap-6">
              <span className="w-20 text-xs text-fg-tertiary font-mono shrink-0">{name}</span>
              <span className="w-12 text-xs text-fg-tertiary shrink-0">{value}</span>
              <span
                className="text-lg"
                style={{
                  fontWeight: Number(value),
                }}
              >
                The quick brown fox jumps over the lazy dog
              </span>
            </div>
          ))}
        </div>
      </Subsection>
    </Section>
  );
}
function SpacingSection() {
  const spacingEntries = Object.entries(spacing).slice(1); // skip 0

  return (
    <Section id="spacing" title="Spacing">
      <Preview>
        <div className="space-y-2">
          {spacingEntries.map(([key, value]) => (
            <div key={key} className="flex items-center gap-4">
              <span className="w-10 text-xs text-fg-tertiary font-mono text-right shrink-0">
                {key}
              </span>
              <span className="w-20 text-xs text-fg-tertiary shrink-0">{value}</span>
              <div
                className="h-4 bg-primary-400 rounded-sm"
                style={{
                  width: value,
                }}
              />
            </div>
          ))}
        </div>
      </Preview>
    </Section>
  );
}
function BorderRadiusSection() {
  return (
    <Section id="borders" title="Border Radius">
      <Preview>
        <div className="flex flex-wrap gap-6">
          {Object.entries(borderRadius).map(([name, value]) => (
            <div key={name} className="text-center">
              <div
                className="w-20 h-20 bg-primary-100 border-2 border-primary-400"
                style={{
                  borderRadius: value,
                }}
              />
              <p className="text-xs font-medium text-fg-secondary mt-2">{name}</p>
              <p className="text-[10px] text-fg-tertiary font-mono">{value}</p>
            </div>
          ))}
        </div>
      </Preview>
    </Section>
  );
}
function ShadowSection() {
  return (
    <Section id="shadows" title="Shadows">
      <Preview>
        <div className="flex flex-wrap gap-8">
          {Object.entries(shadows).map(([name, value]) => (
            <div key={name} className="text-center">
              <div
                className="w-24 h-24 bg-surface rounded-lg"
                style={{
                  boxShadow: value,
                }}
              />
              <p className="text-xs font-medium text-fg-secondary mt-3">{name}</p>
            </div>
          ))}
        </div>
      </Preview>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PRIMITIVE COMPONENT SECTIONS
// ═══════════════════════════════════════════════════════════════════════

function ButtonSection() {
  const variantList = ['primary', 'secondary', 'outline', 'ghost', 'danger'];
  const sizeList = ['sm', 'md', 'lg'];
  return (
    <Section id="button" title="Button">
      <Subsection title="Variants">
        <Preview>
          <div className="flex flex-wrap gap-3">
            {variantList.map((v) => (
              <Button key={v} variant={v}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </Button>
            ))}
          </div>
        </Preview>
      </Subsection>

      <Subsection title="Sizes">
        <Preview>
          <div className="flex flex-wrap items-center gap-3">
            {sizeList.map((s) => (
              <Button key={s} size={s}>
                Size {s}
              </Button>
            ))}
          </div>
        </Preview>
      </Subsection>

      <Subsection title="States">
        <Preview>
          <div className="flex flex-wrap gap-3">
            <Button loading>Loading</Button>
            <Button disabled>Disabled</Button>
            <Button
              leadingIcon={<Plus size={16} />}
            >
              With Icon
            </Button>
          </div>
        </Preview>
      </Subsection>

      <Subsection title="When to use each variant">
        <div className="rounded-lg border border-edge-strong bg-surface p-6 space-y-3 text-sm">
          <div className="flex gap-3 items-start">
            <Button size="sm" className="shrink-0 w-28">Primary</Button>
            <p className="text-fg-secondary">The main action on a page or modal. Use once per context (e.g. "Save", "Generate", "Submit"). Avoid multiple primary buttons side by side.</p>
          </div>
          <div className="flex gap-3 items-start">
            <Button size="sm" variant="secondary" className="shrink-0 w-28">Secondary</Button>
            <p className="text-fg-secondary">High-emphasis alternative actions that aren't the primary CTA (e.g. "Export CSV", "Bulk Edit"). Solid dark fill gives it weight without competing with primary.</p>
          </div>
          <div className="flex gap-3 items-start">
            <Button size="sm" variant="outline" className="shrink-0 w-28">Outline</Button>
            <p className="text-fg-secondary">Cancel, dismiss, and low-emphasis actions. Always use for "Cancel" buttons in modals and forms. Also good for secondary actions in toolbars.</p>
          </div>
          <div className="flex gap-3 items-start">
            <Button size="sm" variant="ghost" className="shrink-0 w-28">Ghost</Button>
            <p className="text-fg-secondary">Minimal emphasis, inline actions. Use in table rows, card actions, or anywhere a button needs to blend in until hovered (e.g. "Revoke", "Edit").</p>
          </div>
          <div className="flex gap-3 items-start">
            <Button size="sm" variant="danger" className="shrink-0 w-28">Danger</Button>
            <p className="text-fg-secondary">Destructive or irreversible actions (e.g. "Delete", "Revoke"). Use sparingly. Pair with a ConfirmDialog for extra safety.</p>
          </div>
        </div>
      </Subsection>

      <Subsection title="Common patterns">
        <Preview label="Modal footer — primary action + outline cancel">
          <div className="flex justify-end gap-3">
            <Button variant="outline">Cancel</Button>
            <Button>Save Changes</Button>
          </div>
        </Preview>
        <Preview label="Destructive confirmation — danger action + outline cancel">
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline">Cancel</Button>
            <Button variant="danger">Delete</Button>
          </div>
        </Preview>
        <Preview label="Page header — single primary CTA">
          <div className="flex justify-between items-center mt-4">
            <span className="text-fg font-medium">Page Title</span>
            <Button size="sm" leadingIcon={<Plus size={16} />}>Add New</Button>
          </div>
        </Preview>
      </Subsection>

      <Subsection title="Code">
        <CodeBlock>{`// Primary CTA
<Button>Save</Button>

// Cancel / dismiss — always use outline
<Button variant="outline">Cancel</Button>

// High-emphasis alternative action
<Button variant="secondary">Export</Button>

// Inline / table row action
<Button variant="ghost" size="sm">Edit</Button>

// Destructive action
<Button variant="danger">Delete</Button>

// With icon and loading state
<Button leadingIcon={<PlusIcon />}>Add Item</Button>
<Button size="sm" loading>Saving...</Button>`}</CodeBlock>
      </Subsection>
    </Section>
  );
}
function InputSection() {
  return (
    <Section id="input" title="Input">
      <Subsection title="Variants">
        <Preview>
          <div className="max-w-md space-y-4">
            <Input label="Default" placeholder="Enter text..." />
            <Input
              label="With helper"
              helperText="This is a helper message"
              placeholder="Enter text..."
            />
            <Input label="With error" error="This field is required" placeholder="Enter text..." />
            <Input label="Disabled" disabled placeholder="Can't edit this" />
          </div>
        </Preview>
      </Subsection>

      <Subsection title="Types">
        <Preview>
          <div className="max-w-md space-y-4">
            <Input label="Email" type="email" placeholder="you@example.com" />
            <Input label="Password" type="password" placeholder="••••••••" />
            <Input label="Number" type="number" placeholder="0" />
          </div>
        </Preview>
      </Subsection>

      <Subsection title="Code">
        <CodeBlock>{`<Input label="Email" type="email" placeholder="you@example.com" />
<Input label="Name" error="Required" />
<Input label="With helper" helperText="We'll never share your email" />
<Input label="Disabled" disabled placeholder="Can't edit" />
<Input label="Search" prefix={<SearchIcon />} />`}</CodeBlock>
      </Subsection>
    </Section>
  );
}
function TextareaSection() {
  return (
    <Section id="textarea" title="Textarea">
      <Preview>
        <div className="max-w-md space-y-4">
          <Textarea label="Default" placeholder="Write something..." />
          <Textarea
            label="With character count"
            maxLength={200}
            placeholder="Limited to 200 characters..."
          />
          <Textarea
            label="With error"
            error="Content is required"
            placeholder="Write something..."
          />
        </div>
      </Preview>

      <CodeBlock>{`<Textarea label="Message" placeholder="Write something..." />
<Textarea label="Bio" maxLength={200} />
<Textarea label="Notes" error="Content is required" />`}</CodeBlock>
    </Section>
  );
}
function SelectSection() {
  return (
    <Section id="select" title="Select">
      <Preview>
        <div className="max-w-md space-y-4">
          <Select label="Default">
            <option value="">Choose an option</option>
            <option value="1">Option 1</option>
            <option value="2">Option 2</option>
            <option value="3">Option 3</option>
          </Select>
          <Select label="With error" error="Selection required">
            <option value="">Choose...</option>
          </Select>
          <Select label="Disabled" disabled>
            <option>Can&apos;t change</option>
          </Select>
        </div>
      </Preview>

      <CodeBlock>{`<Select label="Country">
  <option value="">Choose...</option>
  <option value="us">United States</option>
  <option value="ca">Canada</option>
</Select>
<Select label="Status" error="Selection required" />`}</CodeBlock>
    </Section>
  );
}
function CheckboxSection() {
  return (
    <Section id="checkbox" title="Checkbox">
      <Preview>
        <div className="space-y-4">
          <Checkbox label="Default checkbox" />
          <Checkbox label="With description" description="This is a helpful description" />
          <Checkbox label="Checked" defaultChecked />
          <Checkbox label="With error" error="You must accept the terms" />
          <Checkbox label="Disabled" disabled />
        </div>
      </Preview>

      <CodeBlock>{`<Checkbox label="Accept terms" />
<Checkbox label="Newsletter" description="Get weekly updates" />
<Checkbox label="Required" error="You must accept" />
<Checkbox label="Pre-checked" defaultChecked />`}</CodeBlock>
    </Section>
  );
}
function ToggleSection() {
  const [toggled, setToggled] = useState(false);
  return (
    <Section id="toggle" title="Toggle">
      <Preview>
        <div className="space-y-4">
          <Toggle label="Default toggle" checked={toggled} onChange={setToggled} />
          <Toggle
            label="With description"
            description="Enable email notifications"
            defaultChecked
          />
          <Toggle label="Disabled" disabled />
        </div>
      </Preview>

      <CodeBlock>{`<Toggle label="Notifications" checked={on} onChange={setOn} />
<Toggle label="Dark mode" description="Use dark theme" defaultChecked />
<Toggle label="Disabled" disabled />`}</CodeBlock>
    </Section>
  );
}
function BadgeSection() {
  const variants = ['default', 'success', 'warning', 'danger', 'info'];
  return (
    <Section id="badge" title="Badge">
      <Subsection title="Variants">
        <Preview>
          <div className="flex flex-wrap gap-3">
            {variants.map((v) => (
              <Badge key={v} variant={v}>
                {v}
              </Badge>
            ))}
          </div>
        </Preview>
      </Subsection>

      <Subsection title="Sizes">
        <Preview>
          <div className="flex flex-wrap gap-3 items-center">
            <Badge size="sm">Small</Badge>
            <Badge size="md">Medium</Badge>
          </div>
        </Preview>
      </Subsection>

      <Subsection title="With Dot">
        <Preview>
          <div className="flex flex-wrap gap-3">
            {variants.map((v) => (
              <Badge key={v} variant={v} dot>
                {v}
              </Badge>
            ))}
          </div>
        </Preview>
      </Subsection>

      <Subsection title="Code">
        <CodeBlock>{`<Badge variant="success">Active</Badge>
<Badge variant="warning" size="sm">Pending</Badge>
<Badge variant="danger" dot>Flagged</Badge>
<Badge variant="info">New</Badge>`}</CodeBlock>
      </Subsection>
    </Section>
  );
}
function AvatarSection() {
  const sizes = ['sm', 'md', 'lg', 'xl'];
  const statuses = ['online', 'offline', 'busy', 'away'];
  return (
    <Section id="avatar" title="Avatar">
      <Subsection title="Sizes">
        <Preview>
          <div className="flex items-end gap-4">
            {sizes.map((s) => (
              <Avatar key={s} size={s} initials="SF" />
            ))}
          </div>
        </Preview>
      </Subsection>

      <Subsection title="With Status">
        <Preview>
          <div className="flex gap-4">
            {statuses.map((s) => (
              <Avatar key={s} size="lg" initials="SF" status={s} />
            ))}
          </div>
        </Preview>
      </Subsection>

      <Subsection title="Initials Fallback">
        <Preview>
          <div className="flex gap-4">
            <Avatar size="lg" initials="JD" />
            <Avatar size="lg" initials="AB" />
            <Avatar size="lg" initials="SF" />
          </div>
        </Preview>
      </Subsection>

      <Subsection title="Code">
        <CodeBlock>{`<Avatar size="md" initials="JD" />
<Avatar size="lg" initials="SF" status="online" />
<Avatar size="xl" src="/photo.jpg" />`}</CodeBlock>
      </Subsection>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LAYOUT COMPONENT SECTIONS
// ═══════════════════════════════════════════════════════════════════════

function ContainerSection() {
  return (
    <Section id="container" title="Container">
      <Preview label="Container constrains max width with responsive padding">
        <Container className="bg-surface-secondary border border-edge-strong rounded-lg py-4 text-center text-sm text-fg-secondary">
          Content within Container (max-width constrained)
        </Container>
      </Preview>

      <CodeBlock>{`<Container>
  <YourPageContent />
</Container>`}</CodeBlock>
    </Section>
  );
}
function PageHeaderSection() {
  return (
    <Section id="page-header" title="PageHeader">
      <Subsection title="Basic">
        <Preview>
          <PageHeader title="Page Title" />
        </Preview>
      </Subsection>

      <Subsection title="With Description and Actions">
        <Preview>
          <PageHeader
            title="Campaigns"
            description="Manage your affiliate campaigns"
            actions={<Button size="sm">New Campaign</Button>}
          />
        </Preview>
      </Subsection>

      <Subsection title="Code">
        <CodeBlock>{`<PageHeader title="Dashboard" />
<PageHeader
  title="Campaigns"
  description="Manage your affiliate campaigns"
  actions={<Button size="sm">New Campaign</Button>}
/>`}</CodeBlock>
      </Subsection>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// DATA DISPLAY SECTIONS
// ═══════════════════════════════════════════════════════════════════════

function CardSection() {
  return (
    <Section id="card" title="Card">
      <Subsection title="Variants">
        <Preview>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <h3 className="font-medium">Bordered (default)</h3>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-fg-secondary">Card body content goes here.</p>
              </CardBody>
              <CardFooter>
                <Button size="sm" variant="outline">
                  Action
                </Button>
              </CardFooter>
            </Card>

            <Card variant="elevated">
              <CardHeader>
                <h3 className="font-medium">Elevated</h3>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-fg-secondary">Card body content goes here.</p>
              </CardBody>
              <CardFooter>
                <Button size="sm" variant="outline">
                  Action
                </Button>
              </CardFooter>
            </Card>
          </div>
        </Preview>
      </Subsection>

      <Subsection title="Code">
        <CodeBlock>{`<Card>
  <CardHeader><h3>Title</h3></CardHeader>
  <CardBody><p>Content here.</p></CardBody>
  <CardFooter><Button size="sm">Action</Button></CardFooter>
</Card>
<Card variant="elevated">...</Card>`}</CodeBlock>
      </Subsection>
    </Section>
  );
}
function StatCardSection() {
  return (
    <Section id="stat-card" title="StatCard">
      <Preview>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Revenue" value="$45,231" change={12.5} />
          <StatCard label="Active Users" value="2,420" change={8.1} />
          <StatCard label="Bounce Rate" value="24.3%" change={-3.2} />
          <StatCard label="Commissions" value="$1,234" />
        </div>
      </Preview>

      <CodeBlock>{`<StatCard label="Total Revenue" value="$45,231" change={12.5} />
<StatCard label="Bounce Rate" value="24.3%" change={-3.2} />
<StatCard label="Commissions" value="$1,234" />`}</CodeBlock>
    </Section>
  );
}
function DescriptionListSection() {
  return (
    <Section id="description-list" title="DescriptionList">
      <Preview>
        <DescriptionList
          items={[
            {
              term: 'Name',
              detail: 'John Doe',
            },
            {
              term: 'Email',
              detail: 'john@example.com',
            },
            {
              term: 'Status',
              detail: <Badge variant="success">Active</Badge>,
            },
            {
              term: 'Joined',
              detail: 'January 15, 2025',
            },
            {
              term: 'Campaign',
              detail: 'Default Campaign',
            },
            {
              term: 'Referral Code',
              detail: 'john-doe',
            },
          ]}
          columns={2}
        />
      </Preview>

      <CodeBlock>{`<DescriptionList
  items={[
    { term: "Name", detail: "John Doe" },
    { term: "Status", detail: <Badge variant="success">Active</Badge> },
  ]}
  columns={2}
/>`}</CodeBlock>
    </Section>
  );
}
function EmptyStateSection() {
  return (
    <Section id="empty-state" title="EmptyState">
      <Preview>
        <EmptyState
          icon={<Inbox size={48} />}
          title="No results found"
          description="Try adjusting your search or filter to find what you're looking for."
          action={<Button variant="outline">Clear filters</Button>}
        />
      </Preview>

      <CodeBlock>{`<EmptyState
  icon={<InboxIcon />}
  title="No results found"
  description="Try adjusting your search or filter."
  action={<Button variant="outline">Clear filters</Button>}
/>`}</CodeBlock>
    </Section>
  );
}
function SkeletonSection() {
  return (
    <Section id="skeleton" title="Skeleton">
      <Preview>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Skeleton variant="circle" width={40} height={40} />
            <div className="flex-1 space-y-2">
              <Skeleton variant="line" width="60%" />
              <Skeleton variant="line" width="40%" />
            </div>
          </div>
          <Skeleton variant="rect" height={120} />
          <div className="space-y-2">
            <Skeleton variant="line" />
            <Skeleton variant="line" />
            <Skeleton variant="line" width="80%" />
          </div>
        </div>
      </Preview>

      <CodeBlock>{`<Skeleton variant="circle" width={40} height={40} />
<Skeleton variant="line" width="60%" />
<Skeleton variant="rect" height={120} />`}</CodeBlock>
    </Section>
  );
}
function TabsSection() {
  const [activeTab, setActiveTab] = useState('overview');
  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
    },
    {
      id: 'details',
      label: 'Details',
    },
    {
      id: 'settings',
      label: 'Settings',
      count: 3,
    },
  ];
  return (
    <Section id="tabs" title="Tabs">
      <Preview>
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        <TabPanel tabId="overview" activeTab={activeTab}>
          <p className="text-sm text-fg-secondary">Overview tab content goes here.</p>
        </TabPanel>
        <TabPanel tabId="details" activeTab={activeTab}>
          <p className="text-sm text-fg-secondary">Details tab content goes here.</p>
        </TabPanel>
        <TabPanel tabId="settings" activeTab={activeTab}>
          <p className="text-sm text-fg-secondary">Settings tab content goes here.</p>
        </TabPanel>
      </Preview>

      <CodeBlock>{`const tabs = [
  { id: "overview", label: "Overview" },
  { id: "settings", label: "Settings", count: 3 },
];
<Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
<TabPanel tabId="overview" activeTab={activeTab}>...</TabPanel>`}</CodeBlock>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FEEDBACK SECTIONS
// ═══════════════════════════════════════════════════════════════════════

function AlertSection() {
  const variants = ['info', 'success', 'warning', 'error'];
  return (
    <Section id="alert" title="Alert">
      <Preview>
        <div className="space-y-4">
          {variants.map((v) => (
            <Alert
              key={v}
              variant={v}
              title={`${v.charAt(0).toUpperCase() + v.slice(1)} alert`}
              dismissible
            >
              This is a {v} alert message with additional description text.
            </Alert>
          ))}
        </div>
      </Preview>

      <CodeBlock>{`<Alert variant="info" title="Info" dismissible>
  Helpful information message.
</Alert>
<Alert variant="error" title="Error">
  Something went wrong.
</Alert>`}</CodeBlock>
    </Section>
  );
}
function ModalSection({ showModal, setShowModal }) {
  return (
    <Section id="modal" title="Modal">
      <Preview>
        <Button onClick={() => setShowModal(true)}>Open Modal</Button>
        <Modal open={showModal} onClose={() => setShowModal(false)}>
          <ModalTitle>Example Modal</ModalTitle>
          <ModalBody>
            <p className="text-sm text-fg-secondary">
              This is a modal dialog. It traps focus inside and can be dismissed with Escape or by
              clicking the backdrop.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => setShowModal(false)}>Confirm</Button>
          </ModalFooter>
        </Modal>
      </Preview>

      <CodeBlock>{`<Modal open={showModal} onClose={() => setShowModal(false)}>
  <ModalTitle>Example Modal</ModalTitle>
  <ModalBody><p>Modal content here.</p></ModalBody>
  <ModalFooter>
    <Button variant="outline" onClick={close}>Cancel</Button>
    <Button onClick={confirm}>Confirm</Button>
  </ModalFooter>
</Modal>`}</CodeBlock>
    </Section>
  );
}
function ConfirmDialogSection({ showConfirm, setShowConfirm }) {
  return (
    <Section id="confirm-dialog" title="ConfirmDialog">
      <Preview>
        <div className="flex gap-3">
          <Button onClick={() => setShowConfirm(true)}>Open Confirm</Button>
        </div>
        <ConfirmDialog
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          onConfirm={() => setShowConfirm(false)}
          title="Delete item?"
          message="This action cannot be undone. Are you sure you want to delete this item?"
          destructive
        />
      </Preview>

      <CodeBlock>{`<ConfirmDialog
  open={showConfirm}
  onClose={() => setShowConfirm(false)}
  onConfirm={handleDelete}
  title="Delete item?"
  message="This action cannot be undone."
  destructive
/>`}</CodeBlock>
    </Section>
  );
}
function DropdownSection() {
  return (
    <Section id="dropdown" title="Dropdown">
      <Preview>
        <Dropdown
          trigger={<Button variant="outline">Actions</Button>}
          items={[
            {
              label: 'Edit',
              onClick: () => {},
            },
            {
              label: 'Duplicate',
              onClick: () => {},
            },
            {
              type: 'divider',
            },
            {
              label: 'Delete',
              onClick: () => {},
              destructive: true,
            },
          ]}
        />
      </Preview>

      <CodeBlock>{`<Dropdown
  trigger={<Button variant="outline">Actions</Button>}
  items={[
    { label: "Edit", onClick: handleEdit },
    { label: "Duplicate", onClick: handleDupe },
    { type: "divider" },
    { label: "Delete", onClick: handleDelete, destructive: true },
  ]}
/>`}</CodeBlock>
    </Section>
  );
}
function TooltipSection() {
  const positions = ['top', 'right', 'bottom', 'left'];
  return (
    <Section id="tooltip" title="Tooltip">
      <Preview>
        <div className="flex flex-wrap gap-4 justify-center py-8">
          {positions.map((pos) => (
            <Tooltip key={pos} content={`Tooltip on ${pos}`} position={pos}>
              <Button variant="outline" size="sm">
                {pos}
              </Button>
            </Tooltip>
          ))}
        </div>
      </Preview>

      <CodeBlock>{`<Tooltip content="More info" position="top">
  <Button variant="outline" size="sm">Hover me</Button>
</Tooltip>`}</CodeBlock>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FORM SECTIONS
// ═══════════════════════════════════════════════════════════════════════

function FormSectionSection() {
  return (
    <Section id="form-section" title="FormSection">
      <Preview>
        <FormSection heading="Account Information" description="Update your personal details.">
          <div className="space-y-4">
            <Input label="Full Name" placeholder="John Doe" />
            <Input label="Email" type="email" placeholder="john@example.com" />
          </div>
        </FormSection>
      </Preview>

      <CodeBlock>{`<FormSection heading="Account Info" description="Update your details.">
  <Input label="Full Name" placeholder="John Doe" />
  <Input label="Email" type="email" />
</FormSection>`}</CodeBlock>
    </Section>
  );
}
function FormActionsSection() {
  return (
    <Section id="form-actions" title="FormActions">
      <Preview>
        <div className="space-y-6">
          <FormActions submitLabel="Save Changes" cancelHref="#" />
          <FormActions submitLabel="Saving..." processing cancelHref="#" />
        </div>
      </Preview>

      <CodeBlock>{`<FormActions submitLabel="Save Changes" cancelHref="/back" />
<FormActions submitLabel="Saving..." processing cancelHref="/back" />`}</CodeBlock>
    </Section>
  );
}
function CopyToClipboardSection() {
  return (
    <Section id="copy-to-clipboard" title="CopyToClipboard">
      <Preview>
        <div className="max-w-md">
          <CopyToClipboard text="https://speakflow.com/r/my-referral-code" />
        </div>
      </Preview>

      <CodeBlock>{`<CopyToClipboard text="https://speakflow.com/r/my-code" />`}</CodeBlock>
    </Section>
  );
}
